import React, { useEffect, useMemo, useState } from "react";
import EntityPageScaffold from "../components/entity/EntityPageScaffold";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import CrudListToolbar from "../components/entity/CrudListToolbar";
import ColumnCustomizerModal from "../components/ColumnCustomizerModal";
import { TableActionColumnHeader } from "../components/TableShell";
import BulkUpdateInsurancesModal from "../components/BulkUpdateInsurancesModal";
import InsuranceSummaryStatCards from "../components/InsuranceSummaryStatCards";
import DuplicateSuccessMessage from "../components/DuplicateSuccessMessage";
import DetailSaveStatus from "../components/DetailSaveStatus";
import EntityCardPeekOverlay, { getPeekAnchorRect } from "../components/entity/EntityCardPeekOverlay";
import VendorEntityCard from "../components/VendorEntityCard";
import AssetEntityCard from "../components/AssetEntityCard";
import { useEntityListChrome } from "../shared/hooks/useEntityListChrome";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import { bulkSequentialDelete } from "../shared/utils/bulkSequentialDelete";
import { formatBatchSummaryMessage } from "../shared/utils/batchSummaryMessage";
import { attachValidatedCsvFileImport, downloadCsvTextFile, downloadCsvTextFromString } from "../shared/utils/csvFileHelpers";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { buildInsuranceCsvExportText, INSURANCE_CSV_IMPORT_HEADER_LINE } from "../shared/utils/insuranceCsv";
import InlinePickInput from "../components/InlinePickInput";
import SearchableSelect from "../components/SearchableSelect";
import useBlurAutoSave from "../shared/hooks/useBlurAutoSave";
import uiConfig from "../uiConfig";
import { getMessageForFetchError } from "../shared/utils/apiError";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import { parseCsvDateToIsoOptional } from "../shared/utils/csvDate";
import { parseOptionalNonNegativeFiniteNumber } from "../shared/utils/csvNumber";
import { formatCurrencyUsd } from "../shared/utils/financeDisplay";
import { formatDueDate, getDueDatePart } from "../shared/utils/dueDateTime";
import { getAssetLabel } from "../shared/utils/assetLabel";
import {
  POLICY_STATUSES,
  INSURANCE_TABLE_COLUMNS,
  INSURANCE_TABLE_DEFAULT_VISIBLE,
  INSURANCE_TABLE_KEY,
  INSURANCE_SORT_KEY_TYPES,
  INSURANCE_NUMERIC_COLUMN_IDS,
  isInsuranceExpiredByExpirationDate,
  getInsuranceYearlyPremium,
  summarizeInsuranceYearlyPremium,
} from "../shared/utils/insuranceTable";
import { fetchTablePreferences, saveTablePreferences } from "../shared/api/tablePreferences";
import { bulkUpdateInsurances } from "../shared/api/insurance";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import { IconEdit, IconDuplicate, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import AssetManagementSectionTabs from "../components/AssetManagementSectionTabs";
import { ASSET_TAB_INSURANCE } from "../shared/utils/assetManagementTabs";
import { apiFetch } from "../shared/api/apiFetch";

function normalizeStatus(raw) {
  const s = raw != null ? String(raw).trim() : "";
  if (!s) return "Active";
  const match = POLICY_STATUSES.find((x) => x.toLowerCase() === s.toLowerCase());
  return match || null;
}

function InsuranceManagement() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo?.account_id;
  const currentUserId = userInfo?._id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [statusFilter, setStatusFilter] = useState("");
  const [expirationQuickFilter, setExpirationQuickFilter] = useState(""); // "" | "expired"
  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    importing,
    setImporting,
    importPanelExpanded,
    setImportPanelExpanded,
    searchTerm,
    setSearchTerm,
    sortConfig,
    bannerError,
    setBannerError,
    viewMode,
    setViewMode,
    confirmDialog,
    setConfirmDialog,
    closeConfirmDialog,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: null, direction: "asc" }, initialViewMode: "card" });

  const [visibleColumnIds, setVisibleColumnIds] = useState(() => [...INSURANCE_TABLE_DEFAULT_VISIBLE]);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [columnPrefsSaving, setColumnPrefsSaving] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [detailMode, setDetailMode] = useState("");
  const [detailInsuranceId, setDetailInsuranceId] = useState(null);
  const [detailPolicyNumber, setDetailPolicyNumber] = useState("");
  const [detailPolicyholder, setDetailPolicyholder] = useState("");
  const [detailInsuranceProvider, setDetailInsuranceProvider] = useState("");
  const [detailInsuredAsset, setDetailInsuredAsset] = useState("");
  const [detailAssetId, setDetailAssetId] = useState("");
  const [detailVendorId, setDetailVendorId] = useState("");
  const [detailPolicyPeriod, setDetailPolicyPeriod] = useState("");
  const [detailEffectiveDate, setDetailEffectiveDate] = useState("");
  const [detailExpirationDate, setDetailExpirationDate] = useState("");
  const [detailPolicyPremium, setDetailPolicyPremium] = useState("");
  const [detailPolicyStatus, setDetailPolicyStatus] = useState("Active");
  const [detailActiveField, setDetailActiveField] = useState("");
  const [detailInitialValues, setDetailInitialValues] = useState(null);
  const [assetList, setAssetList] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [linkedPeek, setLinkedPeek] = useState(null);

  useEffect(() => {
    getDocument();
    loadAssetsAndVendors();
    let cancelled = false;
    async function loadPrefs() {
      if (!account_id || !currentUserId) return;
      try {
        const ids = await fetchTablePreferences(account_id, currentUserId, INSURANCE_TABLE_KEY);
        if (cancelled) return;
        if (Array.isArray(ids) && ids.length > 0) {
          const allowed = new Set(INSURANCE_TABLE_COLUMNS.map((c) => c.id));
          const filtered = ids.filter((id) => allowed.has(id));
          if (filtered.length > 0) setVisibleColumnIds(filtered);
        }
      } catch {
        // keep defaults
      }
    }
    loadPrefs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + "/listInsurances", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function loadAssetsAndVendors() {
    if (!account_id) {
      setAssetList([]);
      setVendorList([]);
      return;
    }
    apiFetch(serverUrl + "/listAssets", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setAssetList(Array.isArray(data) ? data : []))
      .catch(() => setAssetList([]));
    apiFetch(serverUrl + "/listVendors", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((r) => r.json())
      .then((data) => setVendorList(Array.isArray(data) ? data : []))
      .catch(() => setVendorList([]));
  }

  const assetById = useMemo(() => {
    const map = {};
    (assetList || []).forEach((a) => {
      if (a && a._id) map[String(a._id)] = a;
    });
    return map;
  }, [assetList]);

  const vendorById = useMemo(() => {
    const map = {};
    (vendorList || []).forEach((v) => {
      if (v && v._id) map[String(v._id)] = v;
    });
    return map;
  }, [vendorList]);

  function formatAssetDisplay(assetId) {
    const id = assetId != null ? String(assetId).trim() : "";
    if (!id) return "";
    const asset = assetById[id];
    return asset ? getAssetLabel(asset) : "Unknown asset";
  }

  function formatVendorDisplay(vendorId) {
    const id = vendorId != null ? String(vendorId).trim() : "";
    if (!id) return "";
    const vendor = vendorById[id];
    if (!vendor) return "Unknown vendor";
    return `${vendor.name || "Vendor"}${vendor.type ? ` (${vendor.type})` : ""}`;
  }

  function openLinkedAssetPeek(assetId, eventOrEl) {
    const id = assetId != null ? String(assetId).trim() : "";
    if (!id) return;
    setLinkedPeek({ type: "asset", id, anchor: getPeekAnchorRect(eventOrEl) });
  }

  function openLinkedVendorPeek(vendorId, eventOrEl) {
    const id = vendorId != null ? String(vendorId).trim() : "";
    if (!id) return;
    setLinkedPeek({ type: "vendor", id, anchor: getPeekAnchorRect(eventOrEl) });
  }

  function closeLinkedPeek() {
    setLinkedPeek(null);
  }

  function renderLinkedEntityField({
    fieldKey,
    label,
    linkedId,
    displayLabel,
    onOpenPeek,
    editor,
  }) {
    const hasLink = !!(linkedId != null && String(linkedId).trim());
    const showEditor = detailMode === "new" || detailActiveField === fieldKey;
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label className="panel-label">{label}</label>
        {showEditor ? (
          editor
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <button
              type="button"
              data-peek-anchor=""
              onClick={(e) => {
                if (hasLink) onOpenPeek(linkedId, e);
                else setDetailActiveField(fieldKey);
              }}
              className="btn-link"
              style={{
                textAlign: "left",
                flex: 1,
                minWidth: 0,
                padding: "6px 0",
                color: hasLink ? "#0d6efd" : "#212529",
                textDecoration: hasLink ? "underline" : "none",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={hasLink ? `View ${label.toLowerCase()} details` : `Select ${label.toLowerCase()}`}
            >
              {displayLabel || "— None"}
            </button>
            <button
              type="button"
              onClick={() => setDetailActiveField(fieldKey)}
              className="btn-link"
              style={iconBtnStyle}
              title={`Change ${label.toLowerCase()}`}
              aria-label={`Change ${label.toLowerCase()}`}
            >
              <IconEdit size={18} />
            </button>
          </div>
        )}
      </div>
    );
  }

  function getProviders() {
    const set = new Set();
    (currentObjs || []).forEach((row) => {
      const c = (row?.insurance_provider != null ? String(row.insurance_provider) : "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  function matchesInsuranceListFilters(obj) {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    const statusLower = (statusFilter || "").toLowerCase().trim();
    const assetLabel = formatAssetDisplay(obj?.asset_id);
    const vendorLabel = formatVendorDisplay(obj?.vendor_id);
    const matchesSearch =
      !searchLower ||
      [
        obj.policy_number,
        obj.policyholder,
        obj.insurance_provider,
        obj.insured_asset,
        obj.policy_period,
        obj.policy_status,
        assetLabel,
        vendorLabel,
      ].some((v) => v != null && String(v).toLowerCase().includes(searchLower));
    if (!matchesSearch) return false;
    if (statusLower) {
      const status = (obj.policy_status != null ? String(obj.policy_status) : "").toLowerCase().trim();
      if (status !== statusLower) return false;
    }
    return true;
  }

  const expiredQuickFilterCount = useMemo(() => {
    let count = 0;
    for (const obj of currentObjs || []) {
      if (!matchesInsuranceListFilters(obj)) continue;
      if (isInsuranceExpiredByExpirationDate(obj?.expiration_date)) count += 1;
    }
    return count;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- count follows search/status list filters
  }, [currentObjs, searchTerm, statusFilter]);

  function getFilteredAndSortedData() {
    let filtered = (currentObjs || []).filter((obj) => {
      if (!matchesInsuranceListFilters(obj)) return false;
      if (expirationQuickFilter === "expired" && !isInsuranceExpiredByExpirationDate(obj?.expiration_date)) {
        return false;
      }
      return true;
    });
    // Computed yearly_premium for sort/display consistency (not persisted).
    filtered = filtered.map((row) => ({
      ...row,
      yearly_premium: getInsuranceYearlyPremium(row),
    }));
    return sortRowsByConfig(filtered, sortConfig, INSURANCE_SORT_KEY_TYPES);
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function openBulkUpdate() {
    if (getSelectedVisibleIds().size === 0) {
      setConfirmDialog({
        open: true,
        title: "Bulk update",
        message: "Please select at least one policy to update",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkUpdateOpen(true);
  }

  async function applyBulkUpdate(fields) {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setBulkUpdateOpen(false);
      return;
    }
    const ids = Array.from(selectedVisibleIds);
    try {
      setBulkUpdating(true);
      const res = await bulkUpdateInsurances({ account_id, _ids: ids, ...fields });
      const matched = res && typeof res.matched === "number" ? res.matched : ids.length;
      const modified = res && typeof res.modified === "number" ? res.modified : 0;
      setConfirmDialog({
        open: true,
        title: "Bulk update completed",
        mode: "alert",
        onConfirm: null,
        message: `Updated ${modified} policy(ies) (matched ${matched}).`,
      });
      setSelectedIds(new Set());
      setBulkUpdateOpen(false);
      getDocument();
    } catch (err) {
      setConfirmDialog({
        open: true,
        title: "Bulk update failed",
        mode: "alert",
        onConfirm: null,
        message: err && err.message ? err.message : "Bulk update failed",
      });
    } finally {
      setBulkUpdating(false);
    }
  }

  function renderColumnCell(colId, row) {
    switch (colId) {
      case "policy_number":
        return (
          <button type="button" className="btn-link" onClick={() => openInsuranceDetail(row, "view")}>
            {row.policy_number || "—"}
          </button>
        );
      case "policyholder":
        return row.policyholder || "—";
      case "insurance_provider":
        return row.insurance_provider || "—";
      case "asset_id": {
        const label = formatAssetDisplay(row.asset_id);
        if (!row.asset_id || !label) return "—";
        return (
          <button
            type="button"
            className="btn-link"
            data-peek-anchor=""
            onClick={(e) => openLinkedAssetPeek(row.asset_id, e)}
            title="View asset details"
          >
            {label}
          </button>
        );
      }
      case "vendor_id": {
        const label = formatVendorDisplay(row.vendor_id);
        if (!row.vendor_id || !label) return "—";
        return (
          <button
            type="button"
            className="btn-link"
            data-peek-anchor=""
            onClick={(e) => openLinkedVendorPeek(row.vendor_id, e)}
            title="View vendor details"
          >
            {label}
          </button>
        );
      }
      case "insured_asset":
        return row.insured_asset || "—";
      case "policy_period":
        return row.policy_period || "—";
      case "effective_date":
        return formatDueDate(row.effective_date) || "—";
      case "expiration_date": {
        const label = formatDueDate(row.expiration_date) || "—";
        if (label === "—") return label;
        if (isInsuranceExpiredByExpirationDate(row.expiration_date)) {
          return <span className="table-cell-expired">{label}</span>;
        }
        return label;
      }
      case "policy_premium":
        return row.policy_premium != null && row.policy_premium !== ""
          ? formatCurrencyUsd(row.policy_premium)
          : "—";
      case "yearly_premium": {
        const yearly =
          row.yearly_premium != null && Number.isFinite(Number(row.yearly_premium))
            ? Number(row.yearly_premium)
            : getInsuranceYearlyPremium(row);
        return yearly != null && Number.isFinite(yearly) ? formatCurrencyUsd(yearly) : "—";
      }
      case "policy_status":
        return row.policy_status || "Active";
      default:
        return row?.[colId] != null && String(row[colId]).trim() !== "" ? String(row[colId]) : "—";
    }
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    const label = currentObj?.policy_number || "this policy";
    setConfirmDialog({
      open: true,
      title: "Delete policy",
      mode: "confirm",
      message: `Are you sure you want to delete "${label}"?`,
      onConfirm: () => {
        apiFetch(serverUrl + "/deleteInsurance", {
          method: "POST",
          body: JSON.stringify({ account_id, _id: id }),
        })
          .then((response) => response.text())
          .then((data) => {
            setConfirmDialog({ open: true, title: "Insurance", message: data, mode: "alert", onConfirm: null });
            const newSelectedIds = new Set(selectedIds);
            newSelectedIds.delete(id);
            setSelectedIds(newSelectedIds);
            getDocument();
          })
          .catch((err) => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function buildPayloadFromDetail() {
    const status = normalizeStatus(detailPolicyStatus) || "Active";
    const premiumRaw = (detailPolicyPremium || "").trim();
    let policy_premium;
    if (premiumRaw) {
      const parsed = parseOptionalNonNegativeFiniteNumber(premiumRaw, { fieldName: "Policy premium" });
      if (!parsed.ok) throw new Error(parsed.error);
      policy_premium = parsed.value;
    }
    return {
      account_id,
      policy_number: (detailPolicyNumber || "").trim(),
      policyholder: (detailPolicyholder || "").trim(),
      insurance_provider: (detailInsuranceProvider || "").trim(),
      insured_asset: (detailInsuredAsset || "").trim(),
      asset_id: (detailAssetId || "").trim(),
      vendor_id: (detailVendorId || "").trim(),
      policy_period: (detailPolicyPeriod || "").trim(),
      effective_date: (detailEffectiveDate || "").trim() || "",
      expiration_date: (detailExpirationDate || "").trim() || "",
      policy_premium: policy_premium !== undefined ? policy_premium : "",
      policy_status: status,
    };
  }

  function duplicateDocument(currentObj) {
    const duplicateNumber = buildDuplicateLabel(currentObj?.policy_number, { fallback: "Policy" });
    apiFetch(serverUrl + "/createInsurance", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        policy_number: duplicateNumber,
        policyholder: (currentObj?.policyholder != null ? String(currentObj.policyholder) : "").trim(),
        insurance_provider: (currentObj?.insurance_provider != null ? String(currentObj.insurance_provider) : "").trim(),
        insured_asset: (currentObj?.insured_asset != null ? String(currentObj.insured_asset) : "").trim(),
        asset_id: (currentObj?.asset_id != null ? String(currentObj.asset_id) : "").trim(),
        vendor_id: (currentObj?.vendor_id != null ? String(currentObj.vendor_id) : "").trim(),
        policy_period: (currentObj?.policy_period != null ? String(currentObj.policy_period) : "").trim(),
        effective_date: currentObj?.effective_date ? getDueDatePart(currentObj.effective_date) : "",
        expiration_date: currentObj?.expiration_date ? getDueDatePart(currentObj.expiration_date) : "",
        policy_premium: currentObj?.policy_premium != null && currentObj.policy_premium !== "" ? currentObj.policy_premium : "",
        policy_status: normalizeStatus(currentObj?.policy_status) || "Active",
      }),
    })
      .then(async (response) => {
        const data = await response.text();
        if (!response.ok) throw new Error(data || "Failed to duplicate policy");
        let created = null;
        try {
          created = JSON.parse(data);
        } catch {
          created = null;
        }
        const openCreated = () => {
          closeConfirmDialog();
          if (created && created._id) {
            openInsuranceDetail(
              {
                _id: created._id,
                policy_number: created.policy_number != null ? String(created.policy_number) : duplicateNumber,
                policyholder: created.policyholder != null ? String(created.policyholder) : "",
                insurance_provider: created.insurance_provider != null ? String(created.insurance_provider) : "",
                insured_asset: created.insured_asset != null ? String(created.insured_asset) : "",
                asset_id: created.asset_id != null ? String(created.asset_id) : "",
                vendor_id: created.vendor_id != null ? String(created.vendor_id) : "",
                policy_period: created.policy_period != null ? String(created.policy_period) : "",
                effective_date: created.effective_date,
                expiration_date: created.expiration_date,
                policy_premium: created.policy_premium,
                policy_status: created.policy_status || "Active",
              },
              "edit"
            );
          }
        };
        setConfirmDialog({
          open: true,
          title: "Policy duplicated",
          message:
            created && created._id ? (
              <DuplicateSuccessMessage linkLabel={duplicateNumber.trim() || "Policy"} onOpenDetail={openCreated} footer={null} />
            ) : (
              `Created "${duplicateNumber}".`
            ),
          onConfirm: null,
          mode: "alert",
        });
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete policies", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete policies",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(serverUrl + "/deleteInsurance", {
              method: "POST",
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  function getDetailFormValues(row) {
    return {
      policy_number: row?.policy_number || "",
      policyholder: row?.policyholder || "",
      insurance_provider: row?.insurance_provider || "",
      insured_asset: row?.insured_asset || "",
      asset_id: row?.asset_id != null ? String(row.asset_id) : "",
      vendor_id: row?.vendor_id != null ? String(row.vendor_id) : "",
      policy_period: row?.policy_period || "",
      effective_date: getDueDatePart(row?.effective_date),
      expiration_date: getDueDatePart(row?.expiration_date),
      policy_premium:
        row?.policy_premium != null && row.policy_premium !== "" && Number.isFinite(Number(row.policy_premium))
          ? String(row.policy_premium)
          : "",
      policy_status: normalizeStatus(row?.policy_status) || "Active",
    };
  }

  function applyDetailFormValues(values) {
    setDetailPolicyNumber(values?.policy_number || "");
    setDetailPolicyholder(values?.policyholder || "");
    setDetailInsuranceProvider(values?.insurance_provider || "");
    setDetailInsuredAsset(values?.insured_asset || "");
    setDetailAssetId(values?.asset_id || "");
    setDetailVendorId(values?.vendor_id || "");
    setDetailPolicyPeriod(values?.policy_period || "");
    setDetailEffectiveDate(values?.effective_date || "");
    setDetailExpirationDate(values?.expiration_date || "");
    setDetailPolicyPremium(values?.policy_premium != null ? String(values.policy_premium) : "");
    setDetailPolicyStatus(values?.policy_status || "Active");
  }

  function openInsuranceDetail(row, mode = "view") {
    setDetailMode(mode);
    if (!row && mode === "new") {
      setDetailInsuranceId(null);
      const initialValues = getDetailFormValues(null);
      setDetailInitialValues(initialValues);
      applyDetailFormValues(initialValues);
      setDetailActiveField("policy_number");
      return;
    }
    if (!row) {
      setDetailMode("");
      return;
    }
    setDetailInsuranceId(row._id);
    const initialValues = getDetailFormValues(row);
    setDetailInitialValues(initialValues);
    applyDetailFormValues(initialValues);
    setDetailActiveField(mode === "edit" ? "policy_number" : "");
  }

  async function persistDetailChanges() {
    const policyNumberVal = (detailPolicyNumber || "").trim();
    if (!policyNumberVal) return;
    let payload;
    try {
      payload = buildPayloadFromDetail();
    } catch (err) {
      setBannerError(err.message || "Invalid field value.");
      return;
    }
    const isNew = detailMode === "new" || !detailInsuranceId;
    const url = isNew ? serverUrl + "/createInsurance" : serverUrl + "/updateInsurance";
    const body = isNew ? payload : { ...payload, _id: detailInsuranceId };

    try {
      const resp = await apiFetch(url, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const text = await resp.text();
      if (!resp.ok) {
        setBannerError(text || "Request failed.");
        return;
      }
      if (isNew) {
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }
        if (data && data._id) {
          setDetailInsuranceId(data._id);
          setDetailMode("edit");
        }
      }
      await getDocument();
      setDetailInitialValues({
        policy_number: payload.policy_number,
        policyholder: payload.policyholder,
        insurance_provider: payload.insurance_provider,
        insured_asset: payload.insured_asset,
        asset_id: payload.asset_id || "",
        vendor_id: payload.vendor_id || "",
        policy_period: payload.policy_period,
        effective_date: payload.effective_date || "",
        expiration_date: payload.expiration_date || "",
        policy_premium: payload.policy_premium !== "" && payload.policy_premium != null ? String(payload.policy_premium) : "",
        policy_status: payload.policy_status,
      });
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    }
  }

  function parseCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const policyNumberIdx = findCsvColumnIndex(headers, ["policy_number", "policy"]);
    const policyholderIdx = findCsvColumnIndex(headers, ["policyholder"]);
    const providerIdx = findCsvColumnIndex(headers, ["insurance_provider", "provider"]);
    const insuredAssetIdx = findCsvColumnIndex(headers, ["insured_asset"]);
    const assetIdIdx = findCsvColumnIndex(headers, ["asset_id", "assetid"]);
    const vendorIdIdx = findCsvColumnIndex(headers, ["vendor_id", "vendorid"]);
    const periodIdx = findCsvColumnIndex(headers, ["policy_period", "period"]);
    const effectiveIdx = findCsvColumnIndex(headers, ["effective_date", "effective"]);
    const expirationIdx = findCsvColumnIndex(headers, ["expiration_date", "expiration", "expiry_date"]);
    const premiumIdx = findCsvColumnIndex(headers, ["policy_premium", "premium"]);
    const statusIdx = findCsvColumnIndex(headers, ["policy_status", "status"]);
    if (policyNumberIdx === -1) {
      throw new Error("CSV must contain a policy_number column");
    }
    const records = [];
    for (const values of rows) {
      records.push({
        policy_number: values[policyNumberIdx] ?? "",
        policyholder: policyholderIdx >= 0 ? values[policyholderIdx] ?? "" : "",
        insurance_provider: providerIdx >= 0 ? values[providerIdx] ?? "" : "",
        insured_asset: insuredAssetIdx >= 0 ? values[insuredAssetIdx] ?? "" : "",
        asset_id: assetIdIdx >= 0 ? values[assetIdIdx] ?? "" : "",
        vendor_id: vendorIdIdx >= 0 ? values[vendorIdIdx] ?? "" : "",
        policy_period: periodIdx >= 0 ? values[periodIdx] ?? "" : "",
        effective_date: effectiveIdx >= 0 ? values[effectiveIdx] ?? "" : "",
        expiration_date: expirationIdx >= 0 ? values[expirationIdx] ?? "" : "",
        policy_premium: premiumIdx >= 0 ? values[premiumIdx] ?? "" : "",
        policy_status: statusIdx >= 0 ? values[statusIdx] ?? "Active" : "Active",
      });
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const policyNumber = (record.policy_number && String(record.policy_number).trim()) || "";
        if (!policyNumber) {
          errorCount++;
          errors.push(`Row ${i + 2}: Missing policy_number`);
          continue;
        }
        const status = normalizeStatus(record.policy_status);
        if (!status) {
          errorCount++;
          errors.push(`Row ${i + 2}: policy_status must be one of ${POLICY_STATUSES.join(", ")}`);
          continue;
        }
        const effectiveParsed = parseCsvDateToIsoOptional(record.effective_date);
        if (!effectiveParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: effective_date: ${effectiveParsed.error}`);
          continue;
        }
        const expirationParsed = parseCsvDateToIsoOptional(record.expiration_date);
        if (!expirationParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: expiration_date: ${expirationParsed.error}`);
          continue;
        }
        const premiumParsed = parseOptionalNonNegativeFiniteNumber(record.policy_premium, { fieldName: "policy_premium" });
        if (!premiumParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${premiumParsed.error}`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + "/createInsurance", {
            method: "POST",
            body: JSON.stringify({
              account_id,
              policy_number: policyNumber,
              policyholder: (record.policyholder != null ? String(record.policyholder) : "").trim(),
              insurance_provider: (record.insurance_provider != null ? String(record.insurance_provider) : "").trim(),
              insured_asset: (record.insured_asset != null ? String(record.insured_asset) : "").trim(),
              asset_id: (record.asset_id != null ? String(record.asset_id) : "").trim(),
              vendor_id: (record.vendor_id != null ? String(record.vendor_id) : "").trim(),
              policy_period: (record.policy_period != null ? String(record.policy_period) : "").trim(),
              effective_date: effectiveParsed.iso || "",
              expiration_date: expirationParsed.iso || "",
              policy_premium: premiumParsed.value !== undefined ? premiumParsed.value : "",
              policy_status: status,
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      setConfirmDialog({
        open: true,
        title: "Import completed",
        message: formatBatchSummaryMessage("Import completed!", successCount, errorCount, errors),
        onConfirm: null,
        mode: "alert",
      });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, "Error importing CSV."));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile("insurance-sample.csv", [
      INSURANCE_CSV_IMPORT_HEADER_LINE,
      "93-KL-N640-0,\"Irudayaraj, Backiaraj\",State Farm Lloyds,\"5143 Autumn HI, Grand Prairie, TX 75052-2447\",,,12 Months,2024-11-01,2025-11-01,1771.00,Active",
      "12-AB-C123-4,\"Doe, Jane\",Allstate,\"100 Main St, Dallas, TX 75201\",,,6 Months,2025-01-15,2025-07-15,890.50,Active",
    ]);
  }

  function exportSelectedInsurancesCsv() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one policy to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const idSet = selectedVisibleIds;
      const rows = (currentObjs || [])
        .filter((row) => row && idSet.has(row._id))
        .sort((a, b) =>
          String(a?.policy_number ?? "").localeCompare(String(b?.policy_number ?? ""), undefined, {
            sensitivity: "base",
          })
        );
      if (rows.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching policies found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const csv = buildInsuranceCsvExportText(rows);
      const datePart = new Date().toISOString().slice(0, 10);
      downloadCsvTextFromString(`insurance-export-${datePart}.csv`, csv);
      setBannerError("");
    } catch (err) {
      setBannerError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  const detailCurrentValues = {
    policy_number: (detailPolicyNumber || "").trim(),
    policyholder: (detailPolicyholder || "").trim(),
    insurance_provider: (detailInsuranceProvider || "").trim(),
    insured_asset: (detailInsuredAsset || "").trim(),
    asset_id: (detailAssetId || "").trim(),
    vendor_id: (detailVendorId || "").trim(),
    policy_period: (detailPolicyPeriod || "").trim(),
    effective_date: (detailEffectiveDate || "").trim(),
    expiration_date: (detailExpirationDate || "").trim(),
    policy_premium: (detailPolicyPremium || "").trim(),
    policy_status: detailPolicyStatus || "Active",
  };
  const detailHasChanges =
    !!detailInitialValues &&
    Object.keys(detailCurrentValues).some((k) => detailCurrentValues[k] !== detailInitialValues[k]);
  const { isSaving: detailSaving, handleFieldBlur: handleDetailFieldBlur, handleFieldCommitKeyDown: handleDetailFieldCommitKeyDown } = useBlurAutoSave({
    hasChanges: detailHasChanges,
    saveFn: persistDetailChanges,
    onExitEdit: () => setDetailActiveField(""),
  });

  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);
  const listSummary = summarizeInsuranceYearlyPremium(filteredRows);
  const expiredListRows = useMemo(() => {
    return (currentObjs || []).filter(
      (obj) => matchesInsuranceListFilters(obj) && isInsuranceExpiredByExpirationDate(obj?.expiration_date)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follows search/status list filters
  }, [currentObjs, searchTerm, statusFilter]);
  const expiredListSummary = summarizeInsuranceYearlyPremium(expiredListRows);
  const providers = getProviders();

  function renderClickableField(fieldKey, label, displayValue, editor) {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <label className="panel-label">{label}</label>
        {detailMode === "new" || detailActiveField === fieldKey ? (
          editor
        ) : (
          <button
            type="button"
            onClick={() => setDetailActiveField(fieldKey)}
            className="btn-link"
            style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500 }}
          >
            {displayValue || "—"}
          </button>
        )}
      </div>
    );
  }

  return (
    <EntityPageScaffold
      title="Insurance"
      titleTabs={<AssetManagementSectionTabs activeTab={ASSET_TAB_INSURANCE} />}
      subtitle="Manage insurance policies, coverage, and premiums."
      bannerError={bannerError}
      onDismissBanner={() => setBannerError("")}
      confirmDialog={confirmDialog}
      onConfirmModal={() => {
        const fn = confirmDialog.onConfirm;
        closeConfirmDialog();
        if (fn) fn();
      }}
      onCancelModal={closeConfirmDialog}
      pageTopAfterModal={
        <>
          <ColumnCustomizerModal
            open={columnModalOpen}
            onClose={() => setColumnModalOpen(false)}
            allColumns={INSURANCE_TABLE_COLUMNS}
            visibleIds={visibleColumnIds}
            onChangeVisibleIds={setVisibleColumnIds}
            saving={columnPrefsSaving}
            onReset={async () => {
              const defaultIds = [...INSURANCE_TABLE_DEFAULT_VISIBLE];
              setVisibleColumnIds(defaultIds);
              if (!account_id || !currentUserId) {
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, currentUserId, INSURANCE_TABLE_KEY, defaultIds);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: "Column preferences",
                  message: err?.message || "Failed to reset column preferences",
                  mode: "alert",
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
              setColumnModalOpen(false);
            }}
            onSave={async (ids) => {
              if (!account_id || !currentUserId) {
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
                return;
              }
              try {
                setColumnPrefsSaving(true);
                await saveTablePreferences(account_id, currentUserId, INSURANCE_TABLE_KEY, ids);
                setVisibleColumnIds(ids);
                setColumnModalOpen(false);
              } catch (err) {
                setConfirmDialog({
                  open: true,
                  title: "Column preferences",
                  message: err?.message || "Failed to save column preferences",
                  mode: "alert",
                  onConfirm: null,
                });
              } finally {
                setColumnPrefsSaving(false);
              }
            }}
          />

          <BulkUpdateInsurancesModal
            open={bulkUpdateOpen}
            onClose={() => {
              if (bulkUpdating) return;
              setBulkUpdateOpen(false);
            }}
            selectedCount={selectedVisibleCount}
            assets={assetList}
            vendors={vendorList}
            loading={bulkUpdating}
            onApply={applyBulkUpdate}
          />

          {(detailMode === "view" || detailMode === "edit" || detailMode === "new") && (
            <div className="detail-modal-overlay">
              <div
                role="button"
                tabIndex={0}
                className="detail-modal-backdrop"
                onClick={() => {
                  closeLinkedPeek();
                  setDetailMode("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    closeLinkedPeek();
                    setDetailMode("");
                  }
                }}
                aria-label="Close"
              />
              <div className="panel detail-modal-panel">
                <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #dee2e6", padding: "12px 16px" }}>
                  <h3 className="panel-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                    {detailMode === "new" ? "New policy" : "Policy details"}
                  </h3>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <DetailSaveStatus isSaving={detailSaving} hasChanges={detailHasChanges} />
                    <button
                      type="button"
                      className="btn-link btn-link-muted"
                      onClick={() => {
                        closeLinkedPeek();
                        setDetailMode("");
                      }}
                      style={{ fontSize: "18px", lineHeight: 1, padding: "4px 8px" }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={{ padding: "12px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 16px" }}>
                    {renderClickableField(
                      "policy_number",
                      "Policy number",
                      detailPolicyNumber,
                      <input
                        autoFocus={detailMode === "new" || detailActiveField === "policy_number"}
                        type="text"
                        value={detailPolicyNumber}
                        onChange={(e) => setDetailPolicyNumber(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                      />
                    )}
                    {renderClickableField(
                      "policyholder",
                      "Policyholder",
                      detailPolicyholder,
                      <input
                        autoFocus={detailActiveField === "policyholder"}
                        type="text"
                        value={detailPolicyholder}
                        onChange={(e) => setDetailPolicyholder(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                      />
                    )}
                    {renderClickableField(
                      "insurance_provider",
                      "Insurance provider",
                      detailInsuranceProvider,
                      <InlinePickInput
                        autoFocus={detailActiveField === "insurance_provider"}
                        value={detailInsuranceProvider}
                        onChange={setDetailInsuranceProvider}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        onOptionSelect={handleDetailFieldBlur}
                        options={providers}
                        placeholder="Enter or pick provider"
                      />
                    )}
                    {renderClickableField(
                      "policy_status",
                      "Policy status",
                      detailPolicyStatus,
                      <InlinePickInput
                        autoFocus={detailActiveField === "policy_status"}
                        value={detailPolicyStatus}
                        onChange={setDetailPolicyStatus}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        onOptionSelect={handleDetailFieldBlur}
                        options={POLICY_STATUSES}
                        placeholder="Select status"
                      />
                    )}
                    {renderLinkedEntityField({
                      fieldKey: "asset_id",
                      label: "Asset",
                      linkedId: detailAssetId,
                      displayLabel: formatAssetDisplay(detailAssetId),
                      onOpenPeek: openLinkedAssetPeek,
                      editor: (
                        <SearchableSelect
                          value={detailAssetId || ""}
                          onChange={(v) => setDetailAssetId(String(v || ""))}
                          options={(assetList || []).map((a) => ({
                            value: String(a._id),
                            label: getAssetLabel(a),
                            keywords: [a.name, a.category].filter(Boolean).join(" "),
                          }))}
                          placeholder="Select asset…"
                          searchPlaceholder="Search asset…"
                          allowEmpty={true}
                          emptyLabel="— None"
                          maxVisibleItems={4}
                          dropdownPortal={true}
                          title="Asset"
                          autoFocus
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                        />
                      ),
                    })}
                    {renderLinkedEntityField({
                      fieldKey: "vendor_id",
                      label: "Vendor",
                      linkedId: detailVendorId,
                      displayLabel: formatVendorDisplay(detailVendorId),
                      onOpenPeek: openLinkedVendorPeek,
                      editor: (
                        <SearchableSelect
                          value={detailVendorId || ""}
                          onChange={(v) => setDetailVendorId(String(v || ""))}
                          options={(vendorList || []).map((v) => ({
                            value: String(v._id),
                            label: `${v.name || "Vendor"}${v.type ? ` (${v.type})` : ""}`,
                            keywords: [v.name, v.type, v.phone, v.email].filter(Boolean).join(" "),
                          }))}
                          placeholder="Select vendor…"
                          searchPlaceholder="Search vendor…"
                          allowEmpty={true}
                          emptyLabel="— None"
                          maxVisibleItems={4}
                          dropdownPortal={true}
                          title="Vendor"
                          autoFocus
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                        />
                      ),
                    })}
                    <div style={{ gridColumn: "1 / span 2", display: "flex", flexDirection: "column" }}>
                      <label className="panel-label">Insured asset (description)</label>
                      {detailMode === "new" || detailActiveField === "insured_asset" ? (
                        <textarea
                          autoFocus={detailActiveField === "insured_asset"}
                          value={detailInsuredAsset}
                          onChange={(e) => setDetailInsuredAsset(e.target.value)}
                          onBlur={handleDetailFieldBlur}
                          onKeyDown={handleDetailFieldCommitKeyDown}
                          rows={2}
                          className="input input-full"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDetailActiveField("insured_asset")}
                          className="btn-link"
                          style={{ textAlign: "left", width: "100%", padding: "6px 0", color: "#212529", textDecoration: "none", fontWeight: 500, whiteSpace: "pre-wrap" }}
                        >
                          {detailInsuredAsset || "—"}
                        </button>
                      )}
                    </div>
                    {renderClickableField(
                      "policy_period",
                      "Policy period",
                      detailPolicyPeriod,
                      <input
                        autoFocus={detailActiveField === "policy_period"}
                        type="text"
                        value={detailPolicyPeriod}
                        onChange={(e) => setDetailPolicyPeriod(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                        placeholder="e.g. 12 Months"
                      />
                    )}
                    {renderClickableField(
                      "policy_premium",
                      "Policy premium",
                      detailPolicyPremium !== "" ? formatCurrencyUsd(detailPolicyPremium) : "",
                      <input
                        autoFocus={detailActiveField === "policy_premium"}
                        type="text"
                        inputMode="decimal"
                        value={detailPolicyPremium}
                        onChange={(e) => setDetailPolicyPremium(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                        placeholder="0.00"
                      />
                    )}
                    {renderClickableField(
                      "effective_date",
                      "Effective date",
                      detailEffectiveDate ? formatDueDate(`${detailEffectiveDate}T00:00:00.000Z`) : "",
                      <input
                        autoFocus={detailActiveField === "effective_date"}
                        type="date"
                        value={detailEffectiveDate}
                        onChange={(e) => setDetailEffectiveDate(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                      />
                    )}
                    {renderClickableField(
                      "expiration_date",
                      "Expiration date",
                      detailExpirationDate ? formatDueDate(`${detailExpirationDate}T00:00:00.000Z`) : "",
                      <input
                        autoFocus={detailActiveField === "expiration_date"}
                        type="date"
                        value={detailExpirationDate}
                        onChange={(e) => setDetailExpirationDate(e.target.value)}
                        onBlur={handleDetailFieldBlur}
                        onKeyDown={handleDetailFieldCommitKeyDown}
                        className="input input-full"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <EntityCardPeekOverlay
            open={!!linkedPeek}
            onClose={closeLinkedPeek}
            stacked
            anchor={linkedPeek?.anchor || null}
          >
            {linkedPeek?.type === "vendor" ? (
              <VendorEntityCard
                vendor={vendorById[linkedPeek.id] || { name: "Unknown vendor" }}
                showCheckbox={false}
                showActions={false}
                ratingReadOnly
              />
            ) : linkedPeek?.type === "asset" ? (
              <AssetEntityCard
                asset={assetById[linkedPeek.id] || { name: "Unknown asset" }}
                showCheckbox={false}
                showActions={false}
              />
            ) : null}
          </EntityCardPeekOverlay>

          <ImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="insurance-csv-import"
            importing={importing}
            columnHint="Columns: policy_number (required), policyholder, insurance_provider, insured_asset, asset_id, vendor_id, policy_period, effective_date, expiration_date (YYYY-MM-DD), policy_premium, policy_status (Active|Expired|Pending|Cancelled)"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />

          <CrudListToolbar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search policy, holder, provider, asset, vendor…"
            onClearSearch={() => setSearchTerm("")}
            middleSlot={
              <>
                <span className="toolbar-label">
                  <label htmlFor="insurance-status-filter">Status:</label>
                </span>
                <select id="insurance-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input input-min-130">
                  <option value="">All</option>
                  {POLICY_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`btn btn-sm ${expirationQuickFilter === "expired" ? "btn-primary-outline active" : "btn-secondary"}`}
                  onClick={() =>
                    setExpirationQuickFilter((prev) => (prev === "expired" ? "" : "expired"))
                  }
                  aria-pressed={expirationQuickFilter === "expired"}
                  title="Show policies whose expiration date is before today"
                >
                  Expired [{expiredQuickFilterCount}]
                </button>
                <span className="toolbar-divider" />
              </>
            }
            importPanelExpanded={importPanelExpanded}
            onToggleImport={() => setImportPanelExpanded(!importPanelExpanded)}
            extraActions={
              <>
                <button
                  type="button"
                  className="btn btn-md btn-primary-outline"
                  disabled={selectedVisibleCount === 0 || exporting}
                  onClick={() => exportSelectedInsurancesCsv()}
                >
                  {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
                </button>
                <button
                  type="button"
                  onClick={openBulkUpdate}
                  disabled={selectedVisibleCount === 0 || bulkUpdating}
                  className="btn btn-md btn-muted"
                >
                  Update{selectedCountSuffix}
                </button>
              </>
            }
            onNew={() => {
              openInsuranceDetail(null, "new");
              setImportPanelExpanded(false);
            }}
            onDeleteSelected={deleteSelected}
            deleteDisabled={selectedVisibleCount === 0 || deleting}
            deleteCountSuffix={selectedCountSuffix}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </>
      }
      list={
        <div className="table-scroll">
          <InsuranceSummaryStatCards
            totalYearlyPremium={listSummary.totalYearlyPremium}
            policyCount={filteredRows.length}
            expiredYearlyPremium={expiredListSummary.totalYearlyPremium}
            expiredPolicyCount={expiredListRows.length}
          />
          {viewMode === "table" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        checked={filteredRows.length > 0 && filteredRows.every((obj) => selectedIds.has(obj._id))}
                        onChange={(e) => handleSelectAll(e, getFilteredAndSortedData)}
                        disabled={filteredRows.length === 0}
                      />
                    </th>
                    {visibleColumnIds.map((colId) => {
                      const col = INSURANCE_TABLE_COLUMNS.find((c) => c.id === colId);
                      const numeric = INSURANCE_NUMERIC_COLUMN_IDS.has(colId);
                      return (
                        <th
                          key={colId}
                          className={`sortable${numeric ? " table-cell-numeric" : ""}`}
                          onClick={() => handleSort(colId)}
                        >
                          <span className="table-th-label">{col ? col.label : colId}</span>
                        </th>
                      );
                    })}
                    <TableActionColumnHeader
                      onCustomizeColumns={() => setColumnModalOpen(true)}
                      customizeDisabled={columnPrefsSaving}
                    />
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr key={row._id}>
                      <td className="text-center">
                        <input type="checkbox" checked={selectedIds.has(row._id)} onChange={(e) => handleSelectOne(row._id, e.target.checked)} />
                      </td>
                      {visibleColumnIds.map((colId) => (
                        <td
                          key={colId}
                          className={INSURANCE_NUMERIC_COLUMN_IDS.has(colId) ? "table-cell-numeric" : undefined}
                        >
                          {renderColumnCell(colId, row)}
                        </td>
                      ))}
                      <td className="text-center">
                        <RowActionIcons>
                          <button type="button" onClick={() => openInsuranceDetail(row, "edit")} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                          <button type="button" onClick={() => duplicateDocument(row)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                          <button type="button" onClick={() => deleteDocument(row)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                        </RowActionIcons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {viewMode === "card" && (
            <div className="cards-wrapper">
              {filteredRows.map((row) => (
                <div key={row._id} className="card">
                  <div className="card-header">
                    <input type="checkbox" checked={selectedIds.has(row._id)} onChange={(e) => handleSelectOne(row._id, e.target.checked)} />
                    <div className="card-header-content">
                      <button type="button" className="btn-link card-title" onClick={() => openInsuranceDetail(row, "view")}>
                        {row.policy_number || "—"}
                      </button>
                      <div className="panel-hint">
                        {[row.insurance_provider, row.policy_status || "Active"].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                  {row.policyholder && <div className="panel-hint card-notes">{row.policyholder}</div>}
                  {(row.asset_id || row.vendor_id) && (
                    <div className="card-meta-muted" style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px", alignItems: "center" }}>
                      {row.asset_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          data-peek-anchor=""
                          onClick={(e) => openLinkedAssetPeek(row.asset_id, e)}
                          title="View asset details"
                          style={{ padding: 0, fontWeight: 500 }}
                        >
                          Asset: {formatAssetDisplay(row.asset_id) || "—"}
                        </button>
                      ) : null}
                      {row.asset_id && row.vendor_id ? <span>·</span> : null}
                      {row.vendor_id ? (
                        <button
                          type="button"
                          className="btn-link"
                          data-peek-anchor=""
                          onClick={(e) => openLinkedVendorPeek(row.vendor_id, e)}
                          title="View vendor details"
                          style={{ padding: 0, fontWeight: 500 }}
                        >
                          Vendor: {formatVendorDisplay(row.vendor_id) || "—"}
                        </button>
                      ) : null}
                    </div>
                  )}
                  {row.insured_asset && <div className="panel-hint card-notes">{row.insured_asset}</div>}
                  <div className="card-meta-muted">
                    {(() => {
                      const period = row.policy_period || "";
                      const effective = formatDueDate(row.effective_date);
                      const expiration = formatDueDate(row.expiration_date);
                      const premium =
                        row.policy_premium != null && row.policy_premium !== ""
                          ? formatCurrencyUsd(row.policy_premium)
                          : "";
                      const yearly = getInsuranceYearlyPremium(row);
                      const yearlyLabel = yearly != null ? `Yearly ${formatCurrencyUsd(yearly)}` : "";
                      const datePart =
                        effective && expiration ? (
                          <>
                            {effective}
                            {" – "}
                            {isInsuranceExpiredByExpirationDate(row.expiration_date) ? (
                              <span className="date-expired">{expiration}</span>
                            ) : (
                              expiration
                            )}
                          </>
                        ) : expiration && isInsuranceExpiredByExpirationDate(row.expiration_date) ? (
                          <span className="date-expired">{expiration}</span>
                        ) : (
                          effective || expiration || null
                        );
                      const parts = [period, datePart, premium, yearlyLabel].filter(Boolean);
                      if (parts.length === 0) return "—";
                      return parts.map((part, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 ? " · " : null}
                          {part}
                        </React.Fragment>
                      ));
                    })()}
                  </div>
                  <div className="card-footer">
                    <RowActionIcons variant="inline">
                    <button type="button" onClick={() => openInsuranceDetail(row, "edit")} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                    <button type="button" onClick={() => duplicateDocument(row)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                    <button type="button" onClick={() => deleteDocument(row)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                    </RowActionIcons>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );
}

export default InsuranceManagement;
